import { cn } from "@/lib/utils"

/** Accent applied to a stat's mono value (same semantics as the old strip). */
type StatsMetalineAccent = "default" | "success" | "danger" | "warning"

interface StatsMetalineItem {
  /** Stable React key + list identity. */
  key: string
  /** Short lowercase-reading descriptor after the value ("total", "this month"). */
  label: React.ReactNode
  /** Mono figure, pre-formatted by the caller. */
  value: React.ReactNode
  /** Optional extra context, rendered dimmer after the label. */
  sub?: React.ReactNode
  /** Value color; defaults to `foreground`. */
  accent?: StatsMetalineAccent
  /**
   * Makes the pair a button (ArtDent 261015 — the coordinator's attention
   * line jumps to the panel each figure counts). Absent, it is plain text.
   */
  onClick?: () => void
}

const ACCENT_VALUE: Record<StatsMetalineAccent, string> = {
  default: "text-foreground",
  success: "text-emerald-600 dark:text-emerald-400",
  danger: "text-red-600 dark:text-red-400",
  warning: "text-amber-600 dark:text-amber-500",
}

/**
 * Header metadata line — the successor to the full-bleed stats card band
 * (the deleted `ApolloStatsStrip`, and `StatsStrip` which now wraps this). Renders the page's headline numbers as
 * a single baseline-aligned line meant to sit directly under the page title,
 * where a subtitle would go: mono value first, dim label after, hairline
 * dividers between pairs. Costs zero extra header height on wide screens and
 * wraps to a second line on narrow ones.
 *
 * Both page shells reserve a slot for it: `PageShell`'s `stats` prop and
 * `ApolloPageHeader`'s `stats` prop render it under the h1/subtitle.
 */
export function StatsMetaline({
  items,
  className,
}: {
  items: StatsMetalineItem[]
  className?: string
}) {
  if (items.length === 0) return null

  return (
    <div className={cn("flex flex-wrap items-baseline gap-y-1.5", className)}>
      {items.map((item, i) => {
        const pairClassName = cn(
          "inline-flex items-baseline gap-[5px]",
          i > 0 && "ml-3.5 border-l border-border pl-3.5"
        )
        const content = (
          <>
            <span
              className={cn(
                "font-mono text-[13.5px] font-medium leading-none tracking-[-0.01em] tabular-nums",
                ACCENT_VALUE[item.accent ?? "default"]
              )}
            >
              {item.value}
            </span>
            <span className="text-[11.5px] leading-none text-text-dim">
              {item.label}
            </span>
            {item.sub != null && (
              <span className="text-[11px] leading-none text-text-dim/70">
                · {item.sub}
              </span>
            )}
          </>
        )

        return item.onClick ? (
          <button
            key={item.key}
            type="button"
            onClick={item.onClick}
            className={cn(
              pairClassName,
              // One pair, one line: the row wraps between pairs, never inside one.
              "whitespace-nowrap rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
          >
            {content}
          </button>
        ) : (
          <span key={item.key} className={pairClassName}>
            {content}
          </span>
        )
      })}
    </div>
  )
}

export type { StatsMetalineItem, StatsMetalineAccent }
